import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Icon } from "../../kit";
import { useViewer } from "../../../state/ViewerProvider";
import { notice, useDomainState, useUi } from "../../../state/editor/useEditor";
import type { LookCategory } from "../../../shared/lookFields";
import type { MaterialKind } from "../../../shared/api-contract";
import { useCaps } from "../CapsProvider";
import { RIGHT_PANEL_W } from "../layout";
import { ScrollArea } from "../ScrollArea";
import {
  BASIC_CONTROLS,
  LEVELS,
  controlFields,
  parseHexColor,
  type BasicControl,
  type ColorDef,
  type StepperDef,
  type ToggleDef,
} from "./macros";
import { ALL_PARTS, type AppliedStep, type BasicEngine, type BasicScope } from "./applyEngine";
import { CLARITY_GRADES } from "./clarity";
import { resolveRow } from "./targets";
import { switchModePatch } from "./mode";
import { useBasicEngine } from "./useBasicEngine";

/**
 * Fine-tune — Basic mode's right panel.
 *
 * Every stepper is a 1–10 level with a picture of each end (2026-10-04, the
 * user's mock-up): [Lesser picture] [− level +] [More picture]. The pictures
 * are real renders of that control at levels 2 and 9
 * (OF/parity/studio-redesign/render-finetune-thumbs.mjs → public/fine-tune/).
 *
 * Choices (which metal, which stone, which background) live on the LEFT as
 * pictures; this side tunes. Nothing here owns a value: every change goes
 * through the Basic engine, which writes through the Advanced rows' own
 * bindings, so the matching Advanced sliders already show it.
 */
export function BasicPanel() {
  const [ui, patchUi] = useUi();
  const engine = useBasicEngine();
  const caps = useCaps();
  const { client } = useViewer();
  const groups = useDomainState("groups");
  const selection = useDomainState("selection");
  const selectedKind = groups.find((g) => g.id === selection)?.kind ?? null;
  // Clarity needs the v62 inclusion model: an older viewer would ignore the
  // maps and every grade would look Flawless.
  const clarityOn = caps.version >= 62;

  // "This part only" is offered only while a part is selected in the view.
  const [onlySelected, setOnlySelected] = useState(false);
  const scope: BasicScope = useMemo(
    () => ({
      gem: onlySelected && selectedKind === "gem" && selection ? selection : "all",
      metal: onlySelected && selectedKind === "metal" && selection ? selection : "all",
    }),
    [onlySelected, selectedKind, selection],
  );

  useEffect(() => engine.setClarityEnabled(clarityOn), [engine, clarityOn]);
  // Advanced's glitter and inclusion sections write the viewer directly and
  // land in no slice: re-read every part each time this panel comes back.
  //
  // Fine-tune is mounted only in Basic, and Basic has no way to close it (no
  // right rail), so it no longer hides itself on `ui.rightTab`. It used to,
  // and a stale saved `followSelection: true` (the default until 2026-09-12)
  // switched `rightTab` to Advanced's "materials" on every part selection:
  // picking a part chip in Metals or Gems made Fine-tune vanish with no way
  // back short of a mode switch.
  useEffect(() => {
    engine.refreshParts();
  }, [engine]);

  const run = (p: Promise<AppliedStep | null>) => {
    p.catch((e: Error) => notice(`Couldn't apply that: ${e.message}`));
  };

  /** Jump to the Advanced rows a step moved, open and flashing. */
  const seeInAdvanced = (rows: string[]) => {
    const resolved = rows.map((r) => ({ path: r, hit: resolveRow(r) })).filter((r) => r.hit);
    const first = resolved[0]?.hit;
    if (!first) return;
    const collapsed = { ...ui.collapsed };
    for (const r of resolved) if (r.hit!.tab === first.tab) collapsed[`${r.hit!.tab}.${r.hit!.section}`] = false;
    patchUi({
      ...switchModePatch(ui, "advanced"),
      rightTab: first.tab,
      lastRightTab: first.tab,
      lastAdvancedTab: first.tab,
      collapsed,
      highlight: { rows: resolved.map((r) => r.path), at: Date.now() },
    });
    // Material rows only render for a selected part of their kind. Select one
    // so the flashing sliders are actually on screen.
    const kind: MaterialKind | null =
      first.section === "diamondRayTracing"
        ? "gem"
        : first.tab === "materials" && (first.section === "material" || first.section === "materialExtensions")
          ? "metal"
          : null;
    if (kind && selectedKind !== kind) {
      const part = groups.find((g) => g.kind === kind);
      if (part) void client.ring.selectGroup(part.id);
    }
  };

  const hasGems = engine.groups("gem").length > 0;
  const hasMetal = engine.groups("metal").length > 0;
  const step = engine.lastStep;
  const partSelected = selectedKind === "gem" || selectedKind === "metal";
  const selectedLabel = groups.find((g) => g.id === selection)?.label;

  return (
    <div
      className="pointer-events-auto absolute top-0 bottom-0 right-0 z-10 overflow-hidden border-l border-ed-border bg-ed-panel"
      style={{ width: RIGHT_PANEL_W }}
      data-basic-panel
    >
      <div className="flex h-full flex-col">
        <div className="flex min-h-[46px] shrink-0 items-center justify-between gap-2 px-4 pt-3.5 pb-2">
          <h2 className="text-[16px] font-semibold leading-5 text-ed-text">Fine-tune</h2>
          {partSelected ? (
            <div role="radiogroup" aria-label="Apply to" className="flex h-7 items-center rounded-lg bg-ed-bg p-[2px]" data-basic-scope>
              {[false, true].map((only) => (
                <button
                  key={String(only)}
                  role="radio"
                  aria-checked={onlySelected === only}
                  onClick={() => setOnlySelected(only)}
                  title={only ? `Only ${selectedLabel ?? "the selected part"}` : "Every part"}
                  className={`h-6 rounded-md px-2 text-[12px] font-medium ${
                    onlySelected === only ? "bg-ed-panel text-ed-text shadow-[0_1px_2px_rgba(0,0,0,0.1)]" : "text-ed-text-2 hover:text-ed-text"
                  }`}
                >
                  {only ? "This part" : "All parts"}
                </button>
              ))}
            </div>
          ) : (
            <span className="text-[12px] text-ed-text-2">All parts</span>
          )}
        </div>

        {/* pr-1.5 + the bar's 6px stable gutter = the 12px on the left. */}
        <ScrollArea className="flex flex-col gap-2.5 pb-3 pl-3 pr-1.5" data-basic-scroll>
          {CARD_ORDER.map((cat) => {
            // Colour CHOICES are pictures on the left; this side tunes.
            const controls = BASIC_CONTROLS.filter(
              (c) => c.category === cat && c.kind !== "swatch" && (c.kind !== "clarity" || clarityOn),
            );
            if (controls.length === 0) return null;
            const material = CARD_MATERIAL[cat];
            const empty = (material === "gem" && !hasGems) || (material === "metal" && !hasMetal);
            const key = `fineTune.${cat}`;
            // Closed until opened (the user's call, 2026-10-04): five open
            // cards were a wall of sliders before anyone had picked a look.
            // The toggle below stores `false` once a card is opened.
            const open = ui.collapsed[key] === false;
            const name = CARD_NAME[cat];
            const fields = [...new Set(controls.flatMap(controlFields))];
            return (
              <section key={cat} className="shrink-0 rounded-xl border border-ed-border bg-ed-panel" data-basic-category={cat}>
                <header className={`flex h-10 items-center justify-between px-1.5 ${open ? "border-b border-ed-border" : ""}`}>
                  <button
                    className="flex h-8 items-center gap-1 rounded-md px-1.5 text-[13px] font-semibold text-ed-text hover:bg-ed-hover"
                    aria-expanded={open}
                    onClick={() => patchUi({ collapsed: { ...ui.collapsed, [key]: open } })}
                    data-basic-card-toggle={cat}
                  >
                    <Icon name={open ? "expand_more" : "chevron_right"} size={18} className="text-ed-text-3" />
                    {name}
                  </button>
                  {!empty && open ? (
                    <button
                      title={`Put ${name.toLowerCase()} back to how it loaded`}
                      disabled={engine.busy}
                      onClick={() => run(engine.resetFields(fields, `Reset ${name.toLowerCase()}`, cat === "diamond"))}
                      className="rounded-md px-1.5 py-0.5 text-[12px] font-medium text-ed-text-2 hover:bg-ed-hover hover:text-ed-text disabled:opacity-40"
                      data-basic-reset-category={cat}
                    >
                      Reset
                    </button>
                  ) : null}
                </header>
                {!open ? null : empty ? (
                  <p className="px-3 py-3 text-[12px] text-ed-text-2">
                    {material === "gem" ? "No stones in this piece." : "No metal parts in this piece."}
                  </p>
                ) : (
                  <div className="divide-y divide-ed-border">
                    {controls.map((c) => (
                      // The last-changed row is TINTED, never given extra
                      // content: an inline caption under it made every row
                      // below jump each time it moved to another control, and
                      // the next click landed on the wrong button.
                      <div
                        key={c.id}
                        className={`px-3 py-2.5 transition-colors ${step?.controlId === c.id ? "bg-ed-active/60" : ""}`}
                        data-basic-control={c.id}
                      >
                        <ControlRow control={c} engine={engine} scope={scope} run={run} />
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
          <button
            onClick={() => run(engine.reset())}
            disabled={engine.busy || !engine.canUndo()}
            className="mt-1 self-center rounded-md px-2 py-1 text-[12px] font-medium text-ed-text-2 hover:bg-ed-hover hover:text-ed-text disabled:opacity-40"
            data-basic-reset
          >
            Reset everything to how it loaded
          </button>
        </ScrollArea>

        {/* What the last step did. Pinned below the scroll area, so showing it
            only shortens the list from the bottom and no control moves. */}
        {step && (step.summary || step.caption) ? (
          <div
            className="flex min-h-[42px] shrink-0 items-center gap-1 border-t border-ed-border px-4 py-2 text-[12px] leading-[17px] text-ed-text-2"
            data-basic-caption
            data-control={step.controlId ?? ""}
            title={step.caption || undefined}
          >
            <span className="min-w-0 flex-1 truncate">
              <b className="font-semibold text-ed-text">{(step.summary ?? step.label).split(" · ")[0]}</b>
              {(step.summary ?? "").includes(" · ") ? ` · ${(step.summary ?? "").split(" · ").slice(1).join(" · ")}` : ""}
            </span>
            {step.rows.length > 0 ? (
              <button
                className="shrink-0 font-semibold text-ed-gold underline-offset-2 hover:underline"
                onClick={() => seeInAdvanced(step.rows)}
                data-basic-see-advanced
              >
                {step.rows.length === 1 ? "See it" : "See them"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** A FIXED order — the approved mockup's (Metal, Diamond, Light), then the
 * rest. Cards that re-sorted to follow the left panel moved under the pointer
 * every time the left side changed; consistency wins. */
const CARD_ORDER: LookCategory[] = ["metal", "diamond", "light", "shadow", "reflections"];
/** Card titles on this side. Background colour moved to the left panel, so
 * the light card is just Light here. */
const CARD_NAME: Record<LookCategory, string> = {
  metal: "Metal",
  diamond: "Diamond",
  light: "Light",
  shadow: "Ground & shadow",
  reflections: "Reflections",
};
/** The parts a card needs; without them it says so instead of showing dead rows. */
const CARD_MATERIAL: Partial<Record<LookCategory, MaterialKind>> = { metal: "metal", diamond: "gem" };

type Run = (p: Promise<AppliedStep | null>) => void;

function ControlRow({ control, engine, scope, run }: { control: BasicControl; engine: BasicEngine; scope: BasicScope; run: Run }) {
  if (control.kind === "stepper") return <LevelRow ctrl={control} engine={engine} scope={scope} run={run} />;
  if (control.kind === "toggle") return <ToggleRow ctrl={control} engine={engine} run={run} />;
  if (control.kind === "color") return <ColorRow ctrl={control} engine={engine} scope={scope} run={run} />;
  if (control.kind === "clarity") return <ClarityRow label={control.label} hint={control.hint} engine={engine} scope={scope} run={run} />;
  return null;
}

function Label({ label, hint, tag }: { label: string; hint?: string; tag?: string }) {
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-[13px] font-medium leading-[18px] text-ed-text">{label}</span>
        {tag ? <span className="shrink-0 text-[11px] leading-4 text-ed-text-3">{tag}</span> : null}
      </div>
      {hint ? <div className="truncate text-[12px] leading-4 text-ed-text-2" title={hint}>{hint}</div> : null}
    </div>
  );
}

/** What a picture shows while its file is missing: the two ends as tone. */
const PICTURE_FALLBACK: Record<"less" | "more", CSSProperties> = {
  less: { background: "linear-gradient(135deg, #fbfaf7 0%, #e4e2dc 100%)" },
  more: { background: "radial-gradient(circle at 35% 30%, #ffffff 0%, #d9c08e 38%, #7a5a22 100%)" },
};

/** A real render of the control at one end; falls back to a tone if it is missing. */
function Thumb({ src, fallback, chosen }: { src: string; fallback: CSSProperties; chosen?: boolean }) {
  const [broken, setBroken] = useState(false);
  return (
    <span
      className={`block aspect-square w-full overflow-hidden rounded-lg border transition-colors ${
        chosen ? "border-ed-gold ring-2 ring-ed-gold/25" : "border-ed-border group-hover:border-ed-line"
      }`}
      style={broken ? fallback : undefined}
    >
      {broken ? null : (
        <img src={src} alt="" draggable={false} loading="lazy" className="h-full w-full object-cover" onError={() => setBroken(true)} />
      )}
    </span>
  );
}

function Picture({
  ctrl,
  side,
  disabled,
  chosen,
  onClick,
}: {
  ctrl: StepperDef;
  side: "less" | "more";
  disabled: boolean;
  chosen: boolean;
  onClick: () => void;
}) {
  const caption = side === "less" ? ctrl.less : ctrl.more;
  return (
    <button
      className="group flex w-11 shrink-0 flex-col items-center gap-1 disabled:cursor-not-allowed disabled:opacity-60"
      disabled={disabled}
      onClick={onClick}
      aria-label={`${ctrl.label}: ${caption}`}
      title={`${ctrl.label}: one step ${caption.toLowerCase()}`}
      data-basic-picture={side}
    >
      <Thumb src={`/fine-tune/${ctrl.id}-${side}.webp`} fallback={PICTURE_FALLBACK[side]} chosen={chosen} />
      <span className="text-[11px] leading-[13px] text-ed-text-2">{caption}</span>
    </button>
  );
}

function LevelRow({ ctrl, engine, scope, run }: { ctrl: StepperDef; engine: BasicEngine; scope: BasicScope; run: Run }) {
  const level = engine.levelOf(ctrl, scope);
  const canLess = !engine.busy && engine.canStep(ctrl, -1, scope);
  const canMore = !engine.busy && engine.canStep(ctrl, 1, scope);
  const less = () => run(engine.press(ctrl, -1, scope));
  const more = () => run(engine.press(ctrl, 1, scope));
  const btn =
    "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-ed-border text-ed-text transition-colors hover:border-ed-line hover:bg-ed-hover disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-ed-border disabled:hover:bg-transparent";
  return (
    <div>
      <Label label={ctrl.label} hint={ctrl.hint} tag={ctrl.global ? "Whole picture" : undefined} />
      <div className="mt-2 flex items-start gap-2">
        <Picture ctrl={ctrl} side="less" disabled={!canLess} chosen={level === 1} onClick={less} />
        <div className="flex min-w-0 flex-1 flex-col items-center gap-2 pt-1.5">
          <div className="flex items-center gap-1.5">
            <button className={btn} title={`One step ${ctrl.less.toLowerCase()}`} aria-label={`${ctrl.label}: ${ctrl.less}`} disabled={!canLess} onClick={less} data-basic-less>
              <Icon name="remove" size={16} />
            </button>
            <LevelInput
              level={level}
              label={ctrl.label}
              disabled={engine.busy || level === null}
              onCommit={(n) => run(engine.setLevel(ctrl, n, scope))}
            />
            <button className={btn} title={`One step ${ctrl.more.toLowerCase()}`} aria-label={`${ctrl.label}: ${ctrl.more}`} disabled={!canMore} onClick={more} data-basic-more>
              <Icon name="add" size={16} />
            </button>
          </div>
          <Meter level={level} label={ctrl.label} />
        </div>
        <Picture ctrl={ctrl} side="more" disabled={!canMore} chosen={level === LEVELS} onClick={more} />
      </div>
    </div>
  );
}

/** The level as text: a number, "Off" while a switch it needs is off, "–" before it is known. */
const levelText = (level: number | null) => (level === null ? "–" : level === 0 ? "Off" : String(level));

/**
 * The typed level. Commits on Enter or blur (typing "10" must not send 1 and
 * then 10); ↑/↓ move one level and commit at once; Escape puts it back.
 */
function LevelInput({
  level,
  label,
  disabled,
  onCommit,
}: {
  level: number | null;
  label: string;
  disabled: boolean;
  onCommit: (n: number) => void;
}) {
  const [text, setText] = useState(levelText(level));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(levelText(level));
  }, [level]);
  const commit = () => {
    const n = Number.parseInt(text.trim(), 10);
    if (!Number.isFinite(n) || n < 1 || n > LEVELS) {
      setText(levelText(level));
      return;
    }
    setText(String(n));
    if (n !== level) onCommit(n);
  };
  return (
    <input
      type="text"
      inputMode="numeric"
      aria-label={`${label} level, 1 to ${LEVELS}`}
      title={`Type a level from 1 to ${LEVELS}`}
      value={text}
      disabled={disabled}
      onFocus={(e) => {
        focused.current = true;
        e.currentTarget.select();
      }}
      onBlur={() => {
        focused.current = false;
        commit();
      }}
      onChange={(e) => setText(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          commit();
          e.currentTarget.select();
        } else if (e.key === "Escape") {
          setText(levelText(level));
          e.currentTarget.blur();
        } else if ((e.key === "ArrowUp" || e.key === "ArrowDown") && level !== null) {
          e.preventDefault();
          const n = Math.min(LEVELS, Math.max(1, (level || 0) + (e.key === "ArrowUp" ? 1 : -1)));
          setText(String(n));
          if (n !== level) onCommit(n);
        }
      }}
      className="h-8 w-11 rounded-lg border border-ed-line bg-white text-center text-[14px] font-semibold tabular-nums text-ed-text outline-none focus:border-ed-accent focus:ring-2 focus:ring-ed-accent/20 disabled:opacity-60"
      data-basic-level
    />
  );
}

/** Ten ticks, gold up to the level — the chosen state at a glance. */
function Meter({ level, label }: { level: number | null; label: string }) {
  const n = level ?? 0;
  return (
    <div
      className="flex w-full max-w-[124px] items-center gap-[3px]"
      role="meter"
      aria-label={`${label} level ${n} of ${LEVELS}`}
      aria-valuemin={0}
      aria-valuemax={LEVELS}
      aria-valuenow={n}
    >
      {Array.from({ length: LEVELS }, (_, i) => (
        <span key={i} className={`h-1 flex-1 rounded-[2px] ${i < n ? "bg-ed-gold" : "bg-ed-border"}`} />
      ))}
    </div>
  );
}

function ColorRow({ ctrl, engine, scope, run }: { ctrl: ColorDef; engine: BasicEngine; scope: BasicScope; run: Run }) {
  const current = engine.read(ctrl.field, scope);
  const value = typeof current === "string" ? current.toLowerCase() : null;
  // While the picker is open the swatch follows it; the colour is applied
  // once, when it closes — one Undo step, not one per pixel of the drag.
  const [draft, setDraft] = useState<string | null>(null);
  const [text, setText] = useState(value ?? "");
  const focused = useRef(false);
  const picker = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!focused.current) setText(value ?? "");
  }, [value]);

  const apply = (hex: string) => {
    if (hex === value) return;
    run(engine.setValues([{ key: ctrl.field, value: hex }], `${ctrl.label} ${hex}`, scope, ctrl.id));
  };
  // React's onChange on a colour input fires per drag tick; the DOM `change`
  // event fires once, when the picker is closed.
  const applyRef = useRef(apply);
  applyRef.current = apply;
  useEffect(() => {
    const el = picker.current;
    if (!el) return;
    const onChange = () => {
      setDraft(null);
      applyRef.current(el.value.toLowerCase());
    };
    el.addEventListener("change", onChange);
    return () => el.removeEventListener("change", onChange);
  }, []);

  const commitText = () => {
    const hex = parseHexColor(text);
    if (!hex) {
      setText(value ?? "");
      return;
    }
    setText(hex);
    apply(hex);
  };
  const shown = draft ?? value ?? "#ffffff";
  return (
    <div>
      <Label label={ctrl.label} hint={ctrl.hint} />
      <div className="mt-2 flex items-center gap-2">
        <label
          className="relative h-11 w-11 shrink-0 cursor-pointer overflow-hidden rounded-lg border border-ed-line"
          style={{ background: shown }}
          title="Pick a colour"
        >
          <input
            ref={picker}
            type="color"
            aria-label={`${ctrl.label} colour`}
            value={shown}
            disabled={engine.busy || value === null}
            onChange={(e) => setDraft(e.target.value.toLowerCase())}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            data-basic-swatch-input
          />
        </label>
        <input
          type="text"
          aria-label={`${ctrl.label} hex code`}
          spellCheck={false}
          placeholder="#b49191"
          value={draft ?? text}
          disabled={engine.busy || value === null}
          onFocus={(e) => {
            focused.current = true;
            e.currentTarget.select();
          }}
          onBlur={() => {
            focused.current = false;
            commitText();
          }}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitText();
            else if (e.key === "Escape") {
              setText(value ?? "");
              e.currentTarget.blur();
            }
          }}
          title="A hex code: #b49191, b49191 or 0xb49191"
          className="h-8 w-[112px] rounded-lg border border-ed-line bg-white px-2 text-[13px] tabular-nums text-ed-text outline-none focus:border-ed-accent focus:ring-2 focus:ring-ed-accent/20 disabled:opacity-60"
          data-basic-hex
        />
      </div>
    </div>
  );
}

function ClarityRow({ label, hint, engine, scope, run }: { label: string; hint: string; engine: BasicEngine; scope: BasicScope; run: Run }) {
  const current = engine.clarity(scope);
  return (
    <div>
      <Label label={label} hint={hint} />
      <div role="radiogroup" aria-label={label} className="mt-2 grid grid-cols-5 gap-1.5">
        {CLARITY_GRADES.map((g) => {
          const chosen = current === g.id;
          return (
            <button
              key={g.id}
              role="radio"
              aria-checked={chosen}
              title={g.hint}
              disabled={engine.busy || current === undefined}
              onClick={() => run(engine.setClarity(g.id, scope))}
              className="group flex min-w-0 flex-col items-center gap-1 disabled:cursor-not-allowed disabled:opacity-60"
              data-basic-clarity={g.id}
            >
              <Thumb src={`/fine-tune/clarity-${g.id}.webp`} fallback={CLARITY_FALLBACK} chosen={chosen} />
              <span className={`max-w-full truncate text-[11px] leading-[13px] ${chosen ? "font-semibold text-ed-text" : "text-ed-text-2"}`}>{g.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const CLARITY_FALLBACK: CSSProperties = { background: "radial-gradient(circle at 40% 35%, #ffffff 0%, #e7e7e3 55%, #b9b9b2 100%)" };

function ToggleRow({ ctrl, engine, run }: { ctrl: ToggleDef; engine: BasicEngine; run: Run }) {
  const on = engine.read(ctrl.field) === true;
  return (
    <div className="flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <Label label={ctrl.label} hint={ctrl.hint} />
      </div>
      <button
        role="switch"
        aria-checked={on}
        aria-label={ctrl.label}
        disabled={engine.busy}
        onClick={() => run(engine.setValues([{ key: ctrl.field, value: !on }], `${ctrl.label} ${!on ? "on" : "off"}`, ALL_PARTS, ctrl.id))}
        className={`relative h-6 w-10 shrink-0 rounded-full transition-colors ${on ? "bg-ed-gold" : "bg-ed-line/40"}`}
        data-basic-toggle={ctrl.id}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "left-[18px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}
